import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Bell, User, FileImage, Palette, AlertTriangle, Book } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const API = '/api';

// Notification type icons
const NOTIFICATION_ICONS = {
  user_signup: User,
  comic_created: FileImage,
  chapter_published: Book,
  artwork_uploaded: Palette,
  content_reported: AlertTriangle,
  novel_created: Book,
};

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
};

const AdminNotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/admin/notifications/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread || 0);
    } catch (error) {
      // Silently fail - user might not be admin
    }
  }, []);

  const fetchRecent = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/admin/notifications?limit=5&status=unread`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentNotifications(response.data.notifications || []);
    } catch (error) {
      // Silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Fetch recent on dropdown open
  const handleOpenChange = (open) => {
    if (open) {
      fetchRecent();
    }
  };

  const markAsRead = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/notifications/mark-read`,
        { notification_ids: [id] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecentNotifications(prev => prev.filter(n => n.id !== id));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      // Silently fail
    }
  };

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative text-gray-300 hover:text-white"
          data-testid="admin-notification-bell"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 bg-red-500 text-white text-xs font-bold"
              data-testid="notification-badge"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="end" 
        className="w-80 bg-zinc-900 border-zinc-700"
        data-testid="notification-dropdown"
      >
        <DropdownMenuLabel className="flex items-center justify-between">
          <span className="text-white">Notifications</span>
          {unreadCount > 0 && (
            <Badge variant="secondary" className="bg-orange-500/20 text-orange-400">
              {unreadCount} new
            </Badge>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-zinc-700" />
        
        {loading ? (
          <div className="p-4 text-center text-zinc-500 text-sm">Loading...</div>
        ) : recentNotifications.length === 0 ? (
          <div className="p-4 text-center text-zinc-500 text-sm">
            {unreadCount === 0 ? 'All caught up!' : 'No recent notifications'}
          </div>
        ) : (
          <>
            {recentNotifications.map((notification) => {
              const IconComponent = NOTIFICATION_ICONS[notification.type] || Bell;
              return (
                <DropdownMenuItem
                  key={notification.id}
                  className="flex items-start gap-3 p-3 cursor-pointer focus:bg-zinc-800"
                  onClick={() => markAsRead(notification.id, { preventDefault: () => {}, stopPropagation: () => {} })}
                >
                  <div className="p-1.5 rounded bg-zinc-800">
                    <IconComponent className="w-4 h-4 text-orange-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white line-clamp-2">{notification.message}</p>
                    <p className="text-xs text-zinc-500 mt-1">
                      {formatTimeAgo(notification.created_at)}
                    </p>
                  </div>
                </DropdownMenuItem>
              );
            })}
          </>
        )}
        
        <DropdownMenuSeparator className="bg-zinc-700" />
        <DropdownMenuItem asChild>
          <Link
            to="/admin/notifications"
            className="w-full text-center text-sm text-orange-400 hover:text-orange-300 py-2"
          >
            View all notifications
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AdminNotificationBell;
